import { useContext, useEffect, useState } from 'react';
import { getTenantInfo as getTenantInfoApi } from 'src/common/api/system';
import { changeTenantInfo } from '@/store/common';
import store from '@/store/store';
import { urlParams } from '@lhb/func';
import UserInfoContext from '@/layout/context';
import { useMethods } from '@lhb/hook';
import { useTenantInfo } from './useTenantInfo';
import { getShareContent } from '../api/common';

interface TenantInfoResponse {
  globalAliasItemVOs: Array<{ code: string; alias: string, defaultName: string }>;
}

/** 定义类型 */
type GlobalAliasCodeEnumType = {
  [key: string]: string;
};

/** 使用大写表示常量对象 */
const GLOBAL_ALIAS_CODE_ENUM: GlobalAliasCodeEnumType = {
  'standardChancePoint': '机会点',
  'standardFranchisee': '加盟商',
  'standardTask': '拓店任务',
  'brandBusinessArea': '品牌商圈',
  'openingArea': '商圈', // 开店区域/商圈
  'manageArea': '管理片区',
  'branchCompany': '分公司',
  'standardStoreType': '门店类型',
  'franchiseeAffiliation': '加盟商归属人',
} as const;

type GlobalAliasCode = keyof typeof GLOBAL_ALIAS_CODE_ENUM;

interface UrlParams {
  shareId?: string;
  [key: string]: string | undefined;
}

/**
 * 从 URL 参数中获取别名
 */
function getAliasFromUrlParams(urlParams: UrlParams, code: GlobalAliasCode): string {
  const { shareId, ...params } = urlParams;

  if (shareId && Object.keys(params).includes(String(code))) {
    const data = GLOBAL_ALIAS_CODE_ENUM[code];
    try {
      return decodeURIComponent(params[code]!) || data || '';
    } catch (error) {
      console.warn(`URL参数解码失败 (${code}):`, error);
      return params[code] || data || '';
    }
  }

  return '';
}

/**
 * 批量获取多个别名的 Hook
 * @description 为方便使用，codes 可不传，默认取自 GLOBAL_ALIAS_CODE_ENUM 的 keys。
 * 能够被用于分享页，如 /expandstore/spotsurveydetail?shareId=f6uuki7xf474，需要 shareId 对应的 content 中包含 globalAliasItemVOs 数组。
 *
 * @demo
    const {
      standardChancePoint: standardChancePointAlias, // 机会点别名
      standardFranchisee: standardFranchiseeAlias, // 加盟商别名
      standardTask: standardTaskAlias, // 拓店任务别名
      brandBusinessArea: brandBusinessAreaAlias, // 品牌商圈别名
      openingArea: openingAreaAlias, // 商圈别名
      manageArea: manageAreaAlias, // 管理片区别名
      branchCompany: branchCompanyAlias, // 分公司别名
      standardStoreType: standardStoreTypeAlias, // 门店类型别名
      franchiseeAffiliation: franchiseeAffiliationAlias, // 加盟商归属人别名
    } = useGlobalAliases();

  const {
    standardChancePoint: standardChancePointAlias, // 机会点别名
    standardFranchisee: standardFranchiseeAlias, // 加盟商别名
    standardTask: standardTaskAlias, // 拓店任务别名
    brandBusinessArea: brandBusinessAreaAlias, // 品牌商圈别名
    openingArea: openingAreaAlias, // 商圈别名
    manageArea: manageAreaAlias, // 管理片区别名
    branchCompany: branchCompanyAlias, // 分公司别名
    standardStoreType: standardStoreTypeAlias, // 门店类型别名
    franchiseeAffiliation: franchiseeAffiliationAlias, // 加盟商归属人别名
  } = useGlobalAliases([
    'standardChancePoint',
    'standardFranchisee',
    'standardTask',
    'brandBusinessArea',
    'openingArea',
    'manageArea',
    'branchCompany',
    'standardStoreType',
    'franchiseeAffiliation',
  ]);
 */
export function useGlobalAliases(codes?: GlobalAliasCode[]): Record<GlobalAliasCode, string> {

  /* 当前租户信息 */
  const { getTenantInfo } = useTenantInfo({ immediate: false });

  const params = urlParams(location.search) || {};

  const _codes = [...Object.keys(GLOBAL_ALIAS_CODE_ENUM), ...(Array.isArray(codes) ? codes : [])];

  const [aliases, setAliases] = useState<Record<GlobalAliasCode, string>>({});

  useEffect(() => {
    methods.getAliases();
  }, []);

  const methods = useMethods({

    /** 获取单个别名 */
    getAlias(code, globalAliasItemVOsMap) {
      // 先检查 URL 参数
      const urlAlias = getAliasFromUrlParams(params, code);
      if (urlAlias) {
        return urlAlias;
      }

      const item = globalAliasItemVOsMap?.[code];
      const _alias = item?.alias || item?.defaultName || GLOBAL_ALIAS_CODE_ENUM[code] || '';

      return _alias;
    },

    /** 获取多个别名 */
    async getAliases() {
      let globalAliasItemVOs: any[] = [];

      if (params.shareId) { // 从短链分享中获取别名
        const res = await getShareContent({ id: params.shareId });
        const { content } = res || {};
        const targetData = content && (typeof content === 'string') ? JSON.parse(content) : { globalAliasItemVOs: [] };
        globalAliasItemVOs = targetData?.globalAliasItemVOs || [];
      } else { // 从租户信息中获取别名
        await getTenantInfo().then(res => {
          globalAliasItemVOs = res?.globalAliasItemVOs || [];
        });
      }
      const globalAliasItemVOsMap = (Array.isArray(globalAliasItemVOs) ? globalAliasItemVOs : []).reduce((prev, cur) => {
        prev[cur.code] = cur;
        return prev;
      }, {} as Record<GlobalAliasCode, { code: string; alias: string, defaultName: string }>);

      const data = (Array.isArray(_codes) ? _codes : []).reduce((prev, cur) => {
        prev[cur] = methods.getAlias(cur, globalAliasItemVOsMap);
        return prev;
      }, {} as Record<GlobalAliasCode, string>);
      setAliases(data);
    },

  });

  return aliases;
}

/**
 * 全局别名hook
 * @description 注意：更换使用 useGlobalAliases
 * @deprecated 请使用 useGlobalAliases 批量获取多个别名（useGlobalAliases 优化了获取用户信息的逻辑）
 * @demo const openingAreaAlias = useGlobalAlias('openingArea');
 */
export function useGlobalAlias(code: GlobalAliasCode) {
  const params = urlParams(location.search) || {};
  // const tenantInfo = useSelector((state: any) => state.common.tenantInfo) || {}; // 如果组件不在 Provider 中，会报错
  const { tenantInfo } = useContext(UserInfoContext) || {};
  const [alias, setAlias] = useState<string>(GLOBAL_ALIAS_CODE_ENUM[code] || '');
  async function getTenantInfo() {
    try {
      const response: TenantInfoResponse = await getTenantInfoApi(); // 和 h5 不是同一个接口
      const data = response || {};

      const item = (data?.globalAliasItemVOs || []).find((item: { code: string; alias: string, defaultName: string }) => item.code === code);
      const _alias = item?.alias || item?.defaultName || GLOBAL_ALIAS_CODE_ENUM[code];

      setAlias(_alias);

      if (_alias) {
        store.dispatch(changeTenantInfo(data || {}));
      }
    } catch (error) {
      console.error('Failed to fetch tenant info:', error);
    }
  }

  useEffect(() => {
    if ((!tenantInfo || !Array.isArray(tenantInfo.globalAliasItemVOs) || !tenantInfo.globalAliasItemVOs.length) && !params.shareId) {
      getTenantInfo();
    } else if (params.shareId && Object.keys(params).includes(String(code))) {
      // console.log('params', params, Object.keys(params).includes(String(code)), decodeURIComponent(params[code]));
      setAlias(getAliasFromUrlParams(params, code));
    } else {
      const item = (tenantInfo.globalAliasItemVOs || []).find((item: { code: string; alias: string }) => item.code === code);
      const _alias = item?.alias || item?.defaultName || GLOBAL_ALIAS_CODE_ENUM[code];
      setAlias(_alias);
    }
  }, []);

  return alias;
}
